import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/api";
import { useLang } from "@/i18n";
import Layout from "@/components/Layout";
import { Plus, Layers, Trash2, Pencil } from "lucide-react";

export default function Programs() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [programs, setPrograms] = useState([]);
  const [expanded, setExpanded] = useState(null);

  const nav = [
    { to: "/coach", key: "dashboard", label: t("nav.dashboard") },
    { to: "/coach/clients", key: "clients", label: t("nav.clients") },
    { to: "/coach/exercises", key: "exercises", label: t("nav.exercises") },
    { to: "/coach/programs", key: "programs", label: t("nav.programs"), end: true },
    { to: "/coach/nutrition", key: "nutrition", label: t("nav.nutrition") },
    { to: "/coach/supplements", key: "supplements", label: t("nav.supplements") },
    { to: "/coach/stretching", key: "stretching", label: t("nav.stretching") },
    { to: "/coach/shop", key: "shop", label: t("nav.shop") },
    { to: "/coach/testimonials", key: "testimonials", label: t("nav.testimonials") },
    { to: "/coach/education", key: "education", label: t("nav.education") },
  ];

  const load = () => api.get("/programs").then((r) => setPrograms(r.data));
  const [searchParams] = useSearchParams();
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const view = searchParams.get("view");
    if (view && programs.length) {
      setExpanded(view);
      setTimeout(() => document.querySelector(`[data-testid="program-detail-${view}"]`)?.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
    }
  }, [searchParams, programs]);

  const remove = async (id) => {
    await api.delete(`/programs/${id}`);
    load();
  };

  return (
    <Layout nav={nav}>
      <div data-testid="programs-page">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <h1 className="text-3xl sm:text-4xl font-black uppercase tracking-tight">{t("pr.title")}</h1>
          <button data-testid="new-program-button" onClick={() => navigate("/coach/programs/new")}
            className="kc-btn-primary !py-2.5 flex items-center gap-2">
            <Plus className="w-4 h-4" /> {t("pr.new")}
          </button>
        </div>
        {programs.length === 0 ? (
          <p className="text-muted-foreground" data-testid="programs-empty">{t("pr.empty")}</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {programs.map((p) => (
              <div key={p._id} className="kc-card kc-card-hover p-5" data-testid="program-card">
                <div className="flex justify-between items-start gap-2">
                  <Layers className="w-6 h-6 text-accent" />
                  <div className="flex gap-1 items-center">
                    <button data-testid={`toggle-program-${p._id}`} onClick={() => setExpanded(expanded === p._id ? null : p._id)}
                      className="kc-btn-ghost !py-1 !px-2.5 text-xs font-bold uppercase tracking-wider text-primary">
                      {expanded === p._id ? t("pr.hide") : t("pr.preview")}
                    </button>
                    <Link data-testid={`edit-program-${p._id}`} to={`/coach/programs/${p._id}/edit`} className="kc-btn-ghost !p-1.5"><Pencil className="w-4 h-4" /></Link>
                    <button data-testid={`delete-program-${p._id}`} onClick={() => remove(p._id)} className="kc-btn-ghost !p-1.5 text-destructive"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
                <h3 className="font-bold text-lg mt-2">{p.name}</h3>
                {p.description && <p className="text-sm text-muted-foreground line-clamp-2 mt-1">{p.description}</p>}
                <p className="text-xs text-muted-foreground mt-3 font-mono">{p.phases?.length || 0} {t("pr.phases")}</p>
                {expanded === p._id && (
                  <div className="mt-3 pt-3 border-t border-border space-y-3" data-testid={`program-detail-${p._id}`}>
                    {(p.phases || []).map((ph, pi) => (
                      <div key={pi}>
                        <p className="font-bold text-sm uppercase tracking-tight">{ph.name}</p>
                        {(ph.months || []).map((m, mi) => (
                          <div key={mi} className="mt-1">
                            {(m.weeks || []).map((w, wi) => (
                              <details key={wi} className="group" data-testid={`program-week-${pi}-${mi}-${wi}`}>
                                <summary className="cursor-pointer list-none [&::-webkit-details-marker]:hidden text-xs font-bold uppercase tracking-wider text-muted-foreground hover:text-foreground py-1">
                                  {m.name} · {w.name}
                                </summary>
                                <div className="ml-2 space-y-2 pb-2">
                                  {(w.routines || []).map((r, ri) => (
                                    <div key={ri} className="bg-secondary/40 rounded-lg p-3">
                                      <p className="text-sm font-bold">{r.name}{r.day ? <span className="text-muted-foreground font-normal"> · {r.day}</span> : null}</p>
                                      <ul className="text-xs text-muted-foreground mt-1 space-y-0.5">
                                        {(r.exercises || []).map((ex, ei) => (
                                          <li key={ei}>{ex.name} — {ex.sets}×{ex.reps} · rec {ex.rest_seconds}s</li>
                                        ))}
                                      </ul>
                                    </div>
                                  ))}
                                </div>
                              </details>
                            ))}
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
